import React, { useState, useMemo, useRef, useEffect } from 'react';
import MetricTooltip, { METRIC_EXPLANATIONS } from '@/components/system/MetricTooltip';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { useEvmPortfolioKpis } from '@/hooks/useEvmPortfolioKpis';
import { Skeleton } from '@/components/ui/skeleton';
import { fmtCurrencyInt } from '@/lib/formatters';
import { applyDemoContractFilter, isDemoMode, filterDemoArray } from '@/config/demoMode';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Building2,
  TrendingUp,
  AlertTriangle,
  Receipt,
  Clock,
  ChevronDown,
  Search,
  Activity,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

const STATUS_MAP: Record<string, string> = {
  rascunho: 'Rascunho',
  ativo: 'Ativo',
  suspenso: 'Suspenso',
  encerrado: 'Encerrado',
  cancelado: 'Cancelado',
};

const STATUS_COLORS: Record<string, string> = {
  ativo: 'hsl(168, 45%, 32%)',
  rascunho: 'hsl(165, 8%, 70%)',
  suspenso: 'hsl(40, 75%, 48%)',
  encerrado: 'hsl(25, 55%, 40%)',
  cancelado: 'hsl(0, 65%, 48%)',
};

const EmpDashboard: React.FC = () => {
  const { activeClusterIds } = useEmpFeatureFlag();
  const { data: evmKpis } = useEvmPortfolioKpis(activeClusterIds);
  const [evmFilterMode, setEvmFilterMode] = useState<'all' | 'single' | 'custom'>('all');
  const [evmSelectedContracts, setEvmSelectedContracts] = useState<Set<string>>(new Set());
  const [evmDropdownOpen, setEvmDropdownOpen] = useState(false);
  const [evmSearch, setEvmSearch] = useState('');
  const evmDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!evmDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (evmDropdownRef.current && !evmDropdownRef.current.contains(e.target as Node)) {
        setEvmDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [evmDropdownOpen]);

  const { data, isLoading } = useQuery({
    queryKey: ['emp-dashboard-full', activeClusterIds, isDemoMode()],
    queryFn: async () => {
    let contratosQ: any = supabase.from('emp_contratos')
      .select('id, nome, status, valor_global, valor_aditivos_acumulado, data_inicio, data_fim_prevista')
      .in('cluster_id', activeClusterIds);
    contratosQ = applyDemoContractFilter(contratosQ, 'id');

    const [contratosRes, desviosRes, alertasRes, boletimsRes, indicadoresRes, cronogramaRes, progressoRes] = await Promise.all([
        contratosQ,
        supabase.from('emp_desvios').select('id, contrato_id, tipo, severidade, status, created_at')
          .eq('status', 'aberto'),
        supabase.from('emp_alertas').select('id, contrato_id, titulo, severidade, status, created_at')
          .in('status', ['aberto', 'reconhecido'])
          .order('created_at', { ascending: false })
          .limit(50),
        supabase.from('emp_boletins_medicao').select('id, contrato_id, status, numero_boletim, valor_bruto, valor_liquido, percentual_fisico_acumulado, percentual_financeiro_acumulado, periodo_inicio, periodo_fim'),
        supabase.from('emp_vw_indicadores_blindados' as any).select('contrato_id, periodo_referencia, spi, cpi, percentual_fisico_previsto, percentual_fisico_realizado, percentual_financeiro_previsto, percentual_financeiro_realizado, pv, ev, ac')
          .order('periodo_referencia', { ascending: true }) as any,
        supabase.from('emp_cronograma_atividades')
          .select('id, versao_id, nome, peso_fisico, data_inicio_prevista, data_fim_prevista, tipo, emp_cronograma_versoes!inner(contrato_id, status)')
          .eq('emp_cronograma_versoes.status', 'vigente')
          .eq('tipo', 'atividade'),
        supabase.from('emp_vw_progresso_fisico_contrato' as any)
          .select('contrato_id, progresso_fisico_pct, valor_earned') as any,
      ]);

      const contratos = contratosRes.data || [];
      const contratoIdSet = new Set(contratos.map((c: any) => c.id));
      // Filter aggregate sources to visible contracts (demo-mode safe + cluster-safe)
      const restrict = (arr: any[] | null): any[] =>
        (arr || []).filter((r: any) => !r.contrato_id || contratoIdSet.has(r.contrato_id));

      return {
        contratos,
        desvios: restrict(desviosRes.data || []),
        alertas: restrict(alertasRes.data || []).slice(0, 5),
        boletins: restrict(boletimsRes.data || []),
        indicadores: restrict(indicadoresRes.data || []),
        cronograma: (cronogramaRes.data || []).filter((r: any) => {
          const cid = r.emp_cronograma_versoes?.contrato_id;
          return cid && contratoIdSet.has(cid);
        }),
        progressoFisico: restrict(progressoRes.data || []),
      };
    },
    enabled: activeClusterIds.length > 0,
  });

  const contratos = data?.contratos || [];
  const alertas = data?.alertas || [];
  const desvios = data?.desvios || [];
  const indicadores = data?.indicadores || [];
  const cronograma = data?.cronograma || [];
  const progressoFisico = (data as any)?.progressoFisico || [];
  // Filter boletins to only those belonging to visible contratos (cluster-scoped)
  const contratoIds = new Set(contratos.map(c => c.id));
  const boletins = (data?.boletins || []).filter(b => contratoIds.has(b.contrato_id));

  // KPIs
  const contratosAtivos = contratos.filter(c => c.status === 'ativo').length;
  const valorTotal = contratos.reduce((s, c) => s + (c.valor_global || 0), 0);
  const desviosAbertos = desvios.length;
  const medicoesPendentes = boletins.filter(b => b.status === 'em_analise').length;

  // Progresso médio — prefer emp_vw_progresso_fisico_contrato (real-time field execution),
  // fallback to emp_indicadores (approved bulletins) for contracts without field data.
  // NOTE: In test environments, these sources may diverge due to mock data injection.
  const progressoMap = new Map<string, number>();
  progressoFisico.forEach((p: any) => {
    if (contratoIds.has(p.contrato_id) && (p.progresso_fisico_pct || 0) > 0) {
      progressoMap.set(p.contrato_id, Number(p.progresso_fisico_pct) || 0);
    }
  });
  // Fallback: fill from indicadores for contracts not in progressoMap
  const latestByContrato = new Map<string, typeof indicadores[0]>();
  indicadores.forEach(ind => {
    const existing = latestByContrato.get(ind.contrato_id);
    if (!existing || ind.periodo_referencia > existing.periodo_referencia) {
      latestByContrato.set(ind.contrato_id, ind);
    }
  });
  const latestIndicadores = Array.from(latestByContrato.values());
  latestIndicadores.forEach(ind => {
    if (!progressoMap.has(ind.contrato_id) && (ind.percentual_fisico_realizado || 0) > 0) {
      progressoMap.set(ind.contrato_id, ind.percentual_fisico_realizado || 0);
    }
  });
  const progressValues = Array.from(progressoMap.values());
  const avgProgress = progressValues.length > 0
    ? Math.round(progressValues.reduce((s, v) => s + v, 0) / progressValues.length)
    : 0;

  // Status distribution chart
  const statusCounts = contratos.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const statusChartData = Object.entries(statusCounts).map(([status, count]) => ({
    name: STATUS_MAP[status] || status,
    value: count,
    fill: STATUS_COLORS[status] || 'hsl(165, 8%, 70%)',
  }));

  // Filtered contract IDs for EVM chart
  const evmContractIds = useMemo(() => {
    if (evmFilterMode === 'all') return new Set(contratos.map(c => c.id));
    return evmSelectedContracts;
  }, [evmFilterMode, evmSelectedContracts, contratos]);

  // Budget chart - S-curve from cronograma + EVM realized curves
  const budgetChartData = useMemo(() => {
    const filteredContratos = contratos.filter(c => evmContractIds.has(c.id));
    const filteredIndicadores = indicadores.filter(i => evmContractIds.has(i.contrato_id));
    const filteredCronograma = cronograma.filter((act: any) => {
      const cid = act.emp_cronograma_versoes?.contrato_id;
      return cid && evmContractIds.has(cid);
    });
    const valorGlobalMap = new Map<string, number>();
    const contratoDataMap = new Map<string, { inicio: string; fim: string }>();
    filteredContratos.forEach(c => {
      valorGlobalMap.set(c.id, c.valor_global || 0);
      contratoDataMap.set(c.id, { inicio: c.data_inicio || '', fim: c.data_fim_prevista || '' });
    });

    const now = new Date().toISOString().slice(0, 7);

    // === PLANNED CURVE: Build from cronograma activities ===
    // Group activities by contract
    const actividadesPorContrato = new Map<string, Array<{ peso: number; inicio: string; fim: string; nome: string }>>();
    const contratosComCronograma = new Set<string>();

    filteredCronograma.forEach((act: any) => {
      const contratoId = act.emp_cronograma_versoes?.contrato_id;
      if (!contratoId || !act.data_inicio_prevista || !act.data_fim_prevista) return;
      const peso = act.peso_fisico || 0;
      if (peso <= 0) return;
      contratosComCronograma.add(contratoId);
      if (!actividadesPorContrato.has(contratoId)) actividadesPorContrato.set(contratoId, []);
      actividadesPorContrato.get(contratoId)!.push({
        peso,
        inicio: act.data_inicio_prevista,
        fim: act.data_fim_prevista,
        nome: act.nome || '',
      });
    });

    // Distribute each activity's peso across its months (linear distribution)
    const planejadoMap = new Map<string, number>(); // periodo -> accumulated planned value

    actividadesPorContrato.forEach((atividades, contratoId) => {
      const vg = valorGlobalMap.get(contratoId) || 0;
      const totalPeso = atividades.reduce((s, a) => s + a.peso, 0);
      if (totalPeso <= 0 || vg <= 0) return;

      atividades.forEach(atv => {
        // Get months spanned by this activity
        const startDate = new Date(atv.inicio + 'T00:00:00');
        const endDate = new Date(atv.fim + 'T00:00:00');
        const months: string[] = [];
        const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        while (cursor <= endMonth) {
          months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
          cursor.setMonth(cursor.getMonth() + 1);
        }
        if (months.length === 0) return;

        // Distribute peso evenly across months, converted to financial value
        const valorPorMes = (atv.peso / totalPeso) * vg / months.length;
        months.forEach(m => {
          planejadoMap.set(m, (planejadoMap.get(m) || 0) + valorPorMes);
        });
      });
    });

    // Fallback: For contracts WITHOUT cronograma, use percentual_fisico_previsto from indicators
    // NOTE: percentual_fisico_previsto is CUMULATIVE per contract, so we compute deltas
    const sortedIndicadores = [...filteredIndicadores].sort((a, b) =>
      (a.periodo_referencia || '').localeCompare(b.periodo_referencia || '')
    );

    // Track last cumulative value per contract for delta calculation
    const lastPrevisto = new Map<string, number>();
    sortedIndicadores.forEach(ind => {
      if (contratosComCronograma.has(ind.contrato_id)) return; // already handled by cronograma
      const periodo = ind.periodo_referencia?.slice(0, 7) || '';
      const vg = valorGlobalMap.get(ind.contrato_id) || 0;
      const cumPct = ind.percentual_fisico_previsto || 0;
      const prevPct = lastPrevisto.get(ind.contrato_id) || 0;
      const deltaPct = Math.max(0, cumPct - prevPct);
      lastPrevisto.set(ind.contrato_id, cumPct);
      const planejado = (deltaPct / 100) * vg;
      planejadoMap.set(periodo, (planejadoMap.get(periodo) || 0) + planejado);
    });

    // === REALIZED CURVES: Agregado + Medido (only up to current month) ===
    // Percentages in emp_indicadores are CUMULATIVE per contract, so we compute deltas
    const realizadoMap = new Map<string, { agregado: number; medido: number }>();
    const lastFisicoReal = new Map<string, number>();
    const lastFinanReal = new Map<string, number>();
    sortedIndicadores.forEach(ind => {
      const periodo = ind.periodo_referencia?.slice(0, 7) || '';
      if (periodo > now) return;
      const vg = valorGlobalMap.get(ind.contrato_id) || 0;
      const cumFisico = ind.percentual_fisico_realizado || 0;
      const cumFinan = ind.percentual_financeiro_realizado || 0;
      const prevFisico = lastFisicoReal.get(ind.contrato_id) || 0;
      const prevFinan = lastFinanReal.get(ind.contrato_id) || 0;
      const deltaFisico = Math.max(0, cumFisico - prevFisico);
      const deltaFinan = Math.max(0, cumFinan - prevFinan);
      lastFisicoReal.set(ind.contrato_id, cumFisico);
      lastFinanReal.set(ind.contrato_id, cumFinan);
      const agregado = (deltaFisico / 100) * vg;
      const medido = (deltaFinan / 100) * vg;
      const existing = realizadoMap.get(periodo) || { agregado: 0, medido: 0 };
      realizadoMap.set(periodo, {
        agregado: existing.agregado + agregado,
        medido: existing.medido + medido,
      });
    });

    // Merge all periods, sort chronologically
    const allPeriods = new Set([...planejadoMap.keys(), ...realizadoMap.keys()]);
    const sortedPeriods = Array.from(allPeriods).sort();

    // Build accumulated result starting from zero
    let accPlanejado = 0, accAgregado = 0, accMedido = 0;
    const result: Array<Record<string, string | number>> = [{
      periodo: 'Início',
      'Valor Financeiro Planejado': 0,
      'Valor Financeiro do Agregado': 0,
      'Valor Financeiro Medido': 0,
    }];

    sortedPeriods.forEach(periodo => {
      accPlanejado += planejadoMap.get(periodo) || 0;
      const real = realizadoMap.get(periodo);
      if (real) {
        accAgregado += real.agregado;
        accMedido += real.medido;
      }

      const point: Record<string, string | number> = {
        periodo,
        'Valor Financeiro Planejado': Math.round(accPlanejado * 100) / 100,
      };
      if (periodo <= now) {
        point['Valor Financeiro do Agregado'] = Math.round(accAgregado * 100) / 100;
        point['Valor Financeiro Medido'] = Math.round(accMedido * 100) / 100;
      }
      result.push(point);
    });

    // Validation logs
    const cronogramaLogs = Array.from(actividadesPorContrato.entries()).slice(0, 3).map(([cId, atividades]) => {
      const vg = valorGlobalMap.get(cId) || 0;
      const totalPeso = atividades.reduce((s, a) => s + a.peso, 0);
      return {
        contrato_id: cId,
        valor_global: vg,
        fonte: 'cronograma',
        total_atividades: atividades.length,
        soma_pesos: totalPeso,
        atividades: atividades.map(a => ({ nome: a.nome, peso: a.peso, inicio: a.inicio, fim: a.fim })),
      };
    });

    const fallbackLogs = Array.from(valorGlobalMap.entries())
      .filter(([cId]) => !contratosComCronograma.has(cId))
      .slice(0, 3)
      .map(([cId, vg]) => {
        const cInds = sortedIndicadores.filter(i => i.contrato_id === cId);
        return {
          contrato_id: cId,
          valor_global: vg,
          fonte: 'indicadores_fallback',
          totalPeriodos: cInds.length,
        };
      });

    console.log('[GeoIntel:DIAG] EVM chart data computed', {
      filterMode: evmFilterMode,
      filteredContractIds: Array.from(evmContractIds),
      totalPeriods: result.length,
      totalContratos: valorGlobalMap.size,
      contratosComCronograma: contratosComCronograma.size,
      contratosFallback: valorGlobalMap.size - contratosComCronograma.size,
      currentMonth: now,
      firstPoint: result[0],
      lastPoint: result[result.length - 1],
      cronogramaValidation: cronogramaLogs,
      fallbackValidation: fallbackLogs,
      timestamp: new Date().toISOString(),
    });

    return result;
  }, [evmContractIds, evmFilterMode, contratos, indicadores, cronograma]);

  // Progress per contract (bar chart)
  const progressChartData = contratos.map(c => {
    const ind = latestByContrato.get(c.id);
    return {
      name: c.nome?.length > 20 ? c.nome.slice(0, 18) + '…' : (c.nome || 'Sem nome'),
      'Previsto': ind?.percentual_fisico_previsto || 0,
      'Realizado': ind?.percentual_fisico_realizado || 0,
    };
  });

  const formatCurrency = (v: number) =>
    v >= 1_000_000
      ? `R$ ${(v / 1_000_000).toFixed(1)}M`
      : fmtCurrencyInt(v);

  const formatDate = (d: string) => {
    const dt = new Date(d);
    const now = new Date();
    const diffMs = now.getTime() - dt.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Hoje';
    if (diffDays === 1) return 'Ontem';
    return `Há ${diffDays} dias`;
  };

  const severityIcon = (sev: string) => {
    const colors: Record<string, string> = {
      critica: 'text-destructive',
      alta: 'text-emp-warm',
      media: 'text-emp-accent',
      baixa: 'text-muted-foreground',
    };
    return colors[sev] || 'text-muted-foreground';
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col gap-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="emp-stat-card"><Skeleton className="h-16" /></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0">
          <div className="emp-card"><Skeleton className="h-full" /></div>
          <div className="emp-card"><Skeleton className="h-full" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3">
      {/* KPI Cards - compact */}
      <div className={`grid grid-cols-2 ${evmKpis ? 'lg:grid-cols-3 xl:grid-cols-6' : 'lg:grid-cols-4'} gap-3 flex-shrink-0`}>
        <KpiCard
          icon={Building2}
          iconBg="bg-emp-primary/10"
          iconColor="text-emp-primary"
          title="Contratos Ativos"
          value={contratosAtivos}
          subtitle={`${contratos.length} total`}
        />
        <KpiCard
          icon={Receipt}
          iconBg="bg-emp-accent/10"
          iconColor="text-emp-accent"
          title="Valor Global"
          value={formatCurrency(valorTotal)}
          subtitle={evmKpis ? `EAC: ${formatCurrency(evmKpis.totalEac)}` : 'em contratos'}
        />
        <KpiCard
          icon={TrendingUp}
          iconBg="bg-emp-warm/10"
          iconColor="text-emp-warm"
          title="Progresso Geral"
          value={`${avgProgress}%`}
          subtitle="físico realizado"
          progress={avgProgress}
          tooltip={METRIC_EXPLANATIONS.progressoFisico}
        />
        <KpiCard
          icon={AlertTriangle}
          iconBg="bg-destructive/8"
          iconColor="text-destructive"
          title="Pendências"
          value={desviosAbertos + medicoesPendentes}
          subtitle={`${desviosAbertos} desvios · ${medicoesPendentes} medições`}
        />
        {evmKpis?.avgSpi != null && (
          <KpiCard
            icon={Activity}
            iconBg={evmKpis.avgSpi >= 1 ? 'bg-emp-primary/10' : evmKpis.avgSpi >= 0.9 ? 'bg-emp-warm/10' : 'bg-destructive/8'}
            iconColor={evmKpis.avgSpi >= 1 ? 'text-emp-primary' : evmKpis.avgSpi >= 0.9 ? 'text-emp-warm' : 'text-destructive'}
            title="SPI (Prazo)"
            value={evmKpis.avgSpi.toFixed(2)}
            subtitle={`${evmKpis.contractCount} contrato${evmKpis.contractCount > 1 ? 's' : ''} EPC`}
            tooltip={METRIC_EXPLANATIONS.spi}
          />
        )}
        {evmKpis?.avgCpi != null && (
          <KpiCard
            icon={BarChart3}
            iconBg={evmKpis.avgCpi >= 1 ? 'bg-emp-primary/10' : evmKpis.avgCpi >= 0.9 ? 'bg-emp-warm/10' : 'bg-destructive/8'}
            iconColor={evmKpis.avgCpi >= 1 ? 'text-emp-primary' : evmKpis.avgCpi >= 0.9 ? 'text-emp-warm' : 'text-destructive'}
            title="CPI (Custo)"
            value={evmKpis.avgCpi.toFixed(2)}
            subtitle="ponderado por BAC"
            tooltip={METRIC_EXPLANATIONS.cpi}
          />
        )}
      </div>

      {/* Charts - fill remaining space equally */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0">
        {/* Status dos Contratos */}
        <div className="emp-card p-4 flex flex-col min-h-0">
          <h3 className="emp-section-title mb-2 flex-shrink-0">Status dos Contratos</h3>
          <div className="flex-1 min-h-0">
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius="40%"
                    outerRadius="65%"
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {statusChartData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 'var(--chart-tooltip-radius)', border: 'none', boxShadow: 'var(--chart-tooltip-shadow)' }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="Nenhum contrato cadastrado" />
            )}
          </div>
        </div>

        {/* Progresso por Contrato */}
        <div className="emp-card p-4 flex flex-col min-h-0">
          <h3 className="emp-section-title mb-2 flex-shrink-0">Progresso Físico por Contrato</h3>
          <div className="flex-1 min-h-0">
            {progressChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={progressChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(165, 8%, 92%)" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip
                    formatter={(v: number) => `${v}%`}
                    contentStyle={{ borderRadius: 'var(--chart-tooltip-radius)', border: 'none', boxShadow: 'var(--chart-tooltip-shadow)' }}
                  />
                  <Bar dataKey="Previsto" fill="hsl(165, 8%, 82%)" radius={[0, 4, 4, 0]} barSize={12} />
                  <Bar dataKey="Realizado" fill="hsl(168, 45%, 32%)" radius={[0, 4, 4, 0]} barSize={12} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="Sem dados de progresso" />
            )}
          </div>
        </div>
      </div>

      {/* Bottom Row - same flex sizing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0">
        {/* Orçamento x Realizado (EVM) */}
        <div className="emp-card p-4 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-2 flex-shrink-0 gap-2 flex-wrap">
            <h3 className="emp-section-title">Orçamento × Realizado</h3>
            <div className="relative" ref={evmDropdownRef}>
              <button
                onClick={() => setEvmDropdownOpen(prev => !prev)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-border bg-background text-xs font-medium text-foreground hover:bg-muted/50 transition-colors"
              >
                {evmFilterMode === 'all'
                  ? `Todos (${contratos.length})`
                  : `${evmSelectedContracts.size} contrato${evmSelectedContracts.size > 1 ? 's' : ''}`}
                <ChevronDown className={`h-3 w-3 transition-transform ${evmDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {evmDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 w-64 rounded-lg border border-border bg-popover shadow-lg">
                  {/* Search */}
                  <div className="p-2 border-b border-border">
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-muted/50">
                      <Search className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <input
                        type="text"
                        value={evmSearch}
                        onChange={e => setEvmSearch(e.target.value)}
                        placeholder="Buscar contrato..."
                        className="bg-transparent text-xs outline-none w-full text-foreground placeholder:text-muted-foreground"
                        autoFocus
                      />
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
                    <button
                      onClick={() => { setEvmFilterMode('all'); setEvmSelectedContracts(new Set()); }}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      Selecionar todos
                    </button>
                    <button
                      onClick={() => { setEvmFilterMode('custom'); setEvmSelectedContracts(new Set()); }}
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      Limpar seleção
                    </button>
                  </div>
                  {/* Contract list */}
                  <div className="max-h-[200px] overflow-y-auto py-1">
                    {contratos
                      .filter(c => !evmSearch || (c.nome || '').toLowerCase().includes(evmSearch.toLowerCase()))
                      .map(c => {
                        const isChecked = evmFilterMode === 'all' || evmSelectedContracts.has(c.id);
                        return (
                          <label
                            key={c.id}
                            className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer"
                          >
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={(checked) => {
                                const allIds = contratos.map(x => x.id);
                                let next: Set<string>;
                                if (evmFilterMode === 'all') {
                                  // Deselecting from "all" → switch to custom with all except this one
                                  next = new Set(allIds.filter(id => id !== c.id));
                                } else {
                                  next = new Set(evmSelectedContracts);
                                  if (checked) next.add(c.id); else next.delete(c.id);
                                }
                                if (next.size === allIds.length) {
                                  setEvmFilterMode('all');
                                  setEvmSelectedContracts(new Set());
                                } else if (next.size === 0) {
                                  setEvmFilterMode('custom');
                                  setEvmSelectedContracts(new Set());
                                } else {
                                  setEvmFilterMode(next.size === 1 ? 'single' : 'custom');
                                  setEvmSelectedContracts(next);
                                }
                              }}
                              className="h-3.5 w-3.5"
                            />
                            <span className="text-xs text-foreground truncate">{c.nome || 'Sem nome'}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 min-h-0">
            {evmFilterMode !== 'all' && evmSelectedContracts.size === 0 ? (
              <EmptyState text="Selecione ao menos um contrato para visualizar o gráfico" />
            ) : budgetChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={budgetChartData} margin={{ left: 10, right: 20 }}>
                  <defs>
                    <linearGradient id="gradPlanejado" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(165, 8%, 70%)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(165, 8%, 70%)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradAgregado" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(168, 45%, 32%)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(168, 45%, 32%)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradMedido" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(25, 55%, 40%)" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(25, 55%, 40%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(165, 8%, 92%)" />
                  <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}k` : String(v)} />
                  <Tooltip
                    formatter={(v: number) => formatCurrency(v)}
                    contentStyle={{ borderRadius: 'var(--chart-tooltip-radius)', border: 'none', boxShadow: 'var(--chart-tooltip-shadow)' }}
                  />
                  <Area type="monotone" dataKey="Valor Financeiro Planejado" stroke="hsl(165, 8%, 70%)" fill="url(#gradPlanejado)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Valor Financeiro do Agregado" stroke="hsl(168, 45%, 32%)" fill="url(#gradAgregado)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Valor Financeiro Medido" stroke="hsl(25, 55%, 40%)" fill="url(#gradMedido)" strokeWidth={2} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState text="Sem dados de indicadores EVM" />
            )}
          </div>
        </div>

        {/* Alertas Recentes */}
        <div className="emp-card p-4 flex flex-col min-h-0">
          <h3 className="emp-section-title mb-2 flex-shrink-0">Alertas Recentes</h3>
          <div className="flex-1 min-h-0 overflow-auto">
            {alertas.length > 0 ? (
              <div className="space-y-2">
                {alertas.map(a => (
                  <div key={a.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-background/60 border border-border/40">
                    <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${severityIcon(a.severidade)}`} strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{a.titulo}</p>
                      <p className="text-xs text-muted-foreground capitalize">{a.severidade}</p>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(a.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="Nenhum alerta aberto" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---- Sub-components ---- */

interface KpiCardProps {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  title: string;
  value: string | number;
  subtitle: string;
  progress?: number;
  tooltip?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon: Icon, iconBg, iconColor, title, value, subtitle, progress, tooltip }) => (
  <div className="emp-stat-card">
    <div className="flex items-start justify-between">
      <div className="space-y-1.5">
        <p className="emp-kpi-label">
          {tooltip ? (
            <MetricTooltip explanation={tooltip}>{title}</MetricTooltip>
          ) : title}
        </p>
        <p className="emp-kpi-value">{value}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <div className={`emp-icon-badge ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={1.5} />
      </div>
    </div>
    {progress !== undefined && (
      <div className="mt-3 w-full h-2 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-emp-primary transition-all duration-500"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
    )}
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
    {text}
  </div>
);

export default EmpDashboard;
